import { Injectable, isDevMode } from '@angular/core';
import { DevicesDevicesDaoService } from 'src/app/dao/devices-dao-services/devices-devices-dao-service/devices-devices-dao.service';
import { Subject, EMPTY } from 'rxjs';
import { UserStorageService } from '../../user-storage-service/user-storage.service';
import { tap } from 'rxjs/internal/operators/tap';
import { map } from 'rxjs/internal/operators/map';
import { UserService } from '../../user-service/user.service';
import { DeviceApplication } from 'src/app/models/device-models/device-application-model/device-application.model';
import { DeviceInfo } from 'src/app/models/device-models/device-info-model/device-info.model';
import { DevicePeripheral } from 'src/app/models/device-models/device-peripheral-model/device-peripheral.model';
import { DeviceBasic } from 'src/app/models/device-models/device-basic-model/device-basic.model';

@Injectable()
export class DevicesDevicesService {

  accessToken = 'DEV';
  refreshDevicesListener = new Subject<any>();
  refreshDeviceListener = new Subject<any>();
  filterDevicesListener = new Subject<any>();
  searchDevicesListener = new Subject<any>();
  exportDevicesListener = new Subject<any>();
  toggleDevicesDispositionListener = new Subject<any>();

  constructor(private devicesDaoService: DevicesDevicesDaoService,
              private userService: UserService,
              private userStorageService: UserStorageService ) { }

  public getDevicesBasic() {
    if (this.userService.accessGranted(this.accessToken)) {
      return this.devicesDaoService.getDevicesBasic(this.userStorageService.getContext(), this.userStorageService.getLang(), this.userStorageService.getUDID()).pipe(
        tap(o => !isDevMode() || console.dir(o)),
        map(o => {
          const devices = new Array<DeviceBasic>();
          o['data']['deviceList'].forEach(
            (item) => {
              const device = new DeviceBasic();
              Object.assign(device, item);
              devices.push(device);
            }
          );
          return devices;
        })
      );
    }
    return EMPTY;
  }

  public getDeviceInfo(internalId) {
    if (this.userService.accessGranted(this.accessToken)) {
      return this.devicesDaoService.getDeviceInfo(this.userStorageService.getContext(), this.userStorageService.getLang(), this.userStorageService.getUDID(), internalId).pipe(
        tap(o => !isDevMode() || console.dir(o)),
        map(o => {
          const device = new DeviceInfo();
          Object.assign(device, o['data']['deviceInfo']);
          device.RemoteAccess = o['data'].remoteAccess || o['data']['deviceInfo'].remoteAccess;

          const deviceApplications = new Array<DeviceApplication>();
          if (o['data']['deviceApplications']) {
            o['data']['deviceApplications'].forEach(
              (app) => {
                deviceApplications.push(Object.assign(new DeviceApplication, app));
              }
            );
          }

          const devicePeripherals = new Array<DevicePeripheral>();
          if (o['data']['devicePeripherals']) {
            o['data']['devicePeripherals'].forEach(
              (peripheral) => {
                const deviceperiperal = new DevicePeripheral();
                Object.assign(deviceperiperal, peripheral);
                devicePeripherals.push(deviceperiperal);
              }
            );
          }

          const deviceLog = new Array<any>();
          Object.assign(deviceLog, o['data']['deviceLog']);

          return { device, deviceApplications, devicePeripherals, deviceLog };
        })
      );
    }
    return EMPTY;
  }

  public addDevice(token, centerInternalId, deviceName) {
    if (this.userService.accessGranted(this.accessToken)) {
      return this.devicesDaoService.addDevice(this.userStorageService.getContext(), this.userStorageService.getLang(), this.userStorageService.getUDID(), token, centerInternalId, deviceName).pipe(
        tap(o => !isDevMode() || console.dir(o)),
        map(o => o)
      );
    }
    return EMPTY;
  }

  public addDeviceWithLicense(token, centerInternalId, deviceName, licenseInternalId) {
    if (this.userService.accessGranted(this.accessToken)) {
      return this.devicesDaoService.addDeviceWithLicense(
        this.userStorageService.getContext(),
        this.userStorageService.getLang(),
        this.userStorageService.getUDID(),
        token,
        centerInternalId,
        deviceName,
        licenseInternalId
      ).pipe(
        tap(o => !isDevMode() || console.dir(o)),
        map(o => o)
      );
    }
    return EMPTY;
  }

  public addIOT(form) {
    if (this.userService.accessGranted(this.accessToken)) {
      return this.devicesDaoService.addIOT(this.userStorageService.getContext(), this.userStorageService.getLang(), this.userStorageService.getUDID(), form.get('selectedCenter').value.InternalId, form.get('type').value, form.get('brand').value, form.get('model').value, form.get('platform').value, form.get('ipAddress').value, form.get('name').value, form.get('serialNumber').value, form.get('iotVersion').value, form.get('urlWebhook').value, form.get('properties').value ? JSON.stringify(this.arrayToObject(form.get('properties').value)) : '').pipe(
        tap(o => !isDevMode() || console.dir(o)),
        map(o => o)
      );
    }
    return EMPTY;
  }

  public addIOTWithLicense(form) {
    if (this.userService.accessGranted(this.accessToken)) {
      return this.devicesDaoService.addIOTWithLicense(this.userStorageService.getContext(), this.userStorageService.getLang(), this.userStorageService.getUDID(), form.get('selectedCenter').value.InternalId, form.get('type').value, form.get('brand').value, form.get('model').value, form.get('platform').value, form.get('ipAddress').value, form.get('name').value, form.get('serialNumber').value, form.get('iotVersion').value, form.get('urlWebhook').value, form.get('properties').value ? JSON.stringify(this.arrayToObject(form.get('properties').value)) : '', form.get('selectedLicense').value.InternalId).pipe(
        tap(o => !isDevMode() || console.dir(o)),
        map(o => o)
      );
    }
    return EMPTY;
  }

  public updateDevice(internalId, deviceName, deviceDescription, deviceNote, deviceLocation, centerInternalId) {
    if (this.userService.accessGranted(this.accessToken)) {
      return this.devicesDaoService.updateDevice(this.userStorageService.getContext(), this.userStorageService.getLang(), this.userStorageService.getUDID(), internalId, deviceName, deviceDescription, deviceNote, deviceLocation, centerInternalId).pipe(
        tap(o => !isDevMode() || console.dir(o)),
        map(o => o)
      );
    }
    return EMPTY;
  }

  public deleteDevice(internalId) {
    if (this.userService.accessGranted(this.accessToken)) {
      return this.devicesDaoService.deleteDevice(this.userStorageService.getContext(), this.userStorageService.getLang(), this.userStorageService.getUDID(), internalId).pipe(
        tap(o => !isDevMode() || console.dir(o)),
        map(o => o)
      );
    }
    return EMPTY;
  }

  public updateDeviceStatus(internalId) {
    if (this.userService.accessGranted(this.accessToken)) {
      return this.devicesDaoService.updateDeviceStatus(this.userStorageService.getContext(), this.userStorageService.getLang(), this.userStorageService.getUDID(), internalId).pipe(
        tap(o => !isDevMode() || console.dir(o)),
        map(o => o['data']['status'])
      );
    }
    return EMPTY;
  }

  public updateBrightness(internalId, value) {
    if (this.userService.accessGranted(this.accessToken)) {
      return this.devicesDaoService.updateBrightness(this.userStorageService.getContext(), this.userStorageService.getLang(), this.userStorageService.getUDID(), internalId, value).pipe(
        tap(o => !isDevMode() || console.dir(o)),
        map(o => o)
      );
    }
    return EMPTY;
  }

  public updateVolume(internalId, value) {
    if (this.userService.accessGranted(this.accessToken)) {
      return this.devicesDaoService.updateVolume(this.userStorageService.getContext(), this.userStorageService.getLang(), this.userStorageService.getUDID(), internalId, value).pipe(
        tap(o => !isDevMode() || console.dir(o)),
        map(o => o)
      );
    }
    return EMPTY;
  }

  public lock(internalId) {
    if (this.userService.accessGranted(this.accessToken)) {
      return this.devicesDaoService.lock(this.userStorageService.getContext(), this.userStorageService.getLang(), this.userStorageService.getUDID(), internalId).pipe(
        tap(o => !isDevMode() || console.dir(o)),
        map(o => o)
      );
    }
    return EMPTY;
  }

  public unlock(internalId) {
    if (this.userService.accessGranted(this.accessToken)) {
      return this.devicesDaoService.unlock(this.userStorageService.getContext(), this.userStorageService.getLang(), this.userStorageService.getUDID(), internalId).pipe(
        tap(o => !isDevMode() || console.dir(o)),
        map(o => o)
      );
    }
    return EMPTY;
  }

  public requestUpdateDevice(internalId) {
    if (this.userService.accessGranted(this.accessToken)) {
      return this.devicesDaoService.requestUpdateDevice(this.userStorageService.getContext(), this.userStorageService.getLang(), this.userStorageService.getUDID(), internalId).pipe(
        tap(o => !isDevMode() || console.dir(o)),
        map(o => o)
      );
    }
    return EMPTY;
  }

  public updateDeviceOwnImage(internalId, rawImage) {
    if (this.userService.accessGranted(this.accessToken)) {
      return this.devicesDaoService.updateDeviceOwnImage(this.userStorageService.getContext(), this.userStorageService.getLang(), this.userStorageService.getUDID(), internalId, rawImage).pipe(
        tap(o => !isDevMode() || console.dir(o)),
        map(o => o)
      );
    }
    return EMPTY;
  }

  public setMasterDevice(internalId, master) {
    if (this.userService.accessGranted(this.accessToken)) {
      return this.devicesDaoService.setMasterDevice(this.userStorageService.getContext(), this.userStorageService.getLang(), this.userStorageService.getUDID(), internalId, master).pipe(
        tap(o => !isDevMode() || console.dir(o)),
        map(o => o)
      );
    }
    return EMPTY;
  }

  public restartDevice(internalId) {
    if (this.userService.accessGranted(this.accessToken)) {
      return this.devicesDaoService.restartDevice(this.userStorageService.getContext(), this.userStorageService.getLang(), this.userStorageService.getUDID(), internalId).pipe(
        tap(o => !isDevMode() || console.dir(o)),
        map(o => o)
      );
    }
    return EMPTY;
  }

  public launchRemoteAccess(internalId) {
    if (this.userService.accessGranted(this.accessToken)) {
      return this.devicesDaoService.launchRemoteAccess(
        this.userStorageService.getContext(),
        this.userStorageService.getLang(),
        this.userStorageService.getUDID(),
        internalId
      ).pipe(
        map(response => {
          const launchUrl = response && response.data && (response.data.launchUrl || response.data.url);
          const successfulResponse = response && (response.status === 'OK' || String(response.status) === '200');
          if (!successfulResponse || !launchUrl || !/^https?:\/\//i.test(launchUrl)) {
            throw new Error('Invalid remote access launch response');
          }
          return launchUrl;
        })
      );
    }
    return EMPTY;
  }

  public listOfPeripheralTypes() {
    if (this.userService.accessGranted(this.accessToken)) {
      return this.devicesDaoService.listOfPeripheralTypes(this.userStorageService.getContext(), this.userStorageService.getLang(), this.userStorageService.getUDID()).pipe(
        tap(o => !isDevMode() || console.dir(o)),
        map(o => {
          return {
            peripheralTypes: o['data']['peripheralTypes']['peripheralTypes'],
            peripheralConnectionTypes: o['data']['peripheralTypes']['peripheralConnectionTypes']
          };
        })
      );
    }
    return EMPTY;
  }

  public getPeripheral(internalId) {
    if (this.userService.accessGranted(this.accessToken)) {
      return this.devicesDaoService.getPeripheral(this.userStorageService.getContext(), this.userStorageService.getLang(), this.userStorageService.getUDID(), internalId).pipe(
        tap(o => !isDevMode() || console.dir(o)),
        map(o => {
          const peripheral = new DevicePeripheral();
          Object.assign(peripheral, o['data']['peripheralDetails']);
          return peripheral;
        })
      );
    }
    return EMPTY;
  }

  public savePeripheral(active, internalId, deviceInternalId, peripheralName, peripheralType, peripheralConnectionType, peripheralConnectionPort, vendorId, productId, comBaud, btName, btMac, btPassword, tcpIp) {
    if (this.userService.accessGranted(this.accessToken)) {
      return this.devicesDaoService.savePeripheral(this.userStorageService.getContext(), this.userStorageService.getLang(), this.userStorageService.getUDID(), active, internalId, deviceInternalId, peripheralName, peripheralType, peripheralConnectionType, peripheralConnectionPort, vendorId, productId, comBaud, btName, btMac, btPassword, tcpIp).pipe(
        tap(o => !isDevMode() || console.dir(o)),
        map(o => o)
      );
    }
    return EMPTY;
  }

  public updateStatusPeripheral(internalId) {
    if (this.userService.accessGranted(this.accessToken)) {
      return this.devicesDaoService.updateStatusPeripheral(this.userStorageService.getContext(), this.userStorageService.getLang(), this.userStorageService.getUDID(), internalId).pipe(
        tap(o => !isDevMode() || console.dir(o)),
        map(o => o)
      );
    }
    return EMPTY;
  }

  public deletePeripheral(internalId) {
    if (this.userService.accessGranted(this.accessToken)) {
      return this.devicesDaoService.deletePeripheral(this.userStorageService.getContext(), this.userStorageService.getLang(), this.userStorageService.getUDID(), internalId).pipe(
        tap(o => !isDevMode() || console.dir(o)),
        map(o => o)
      );
    }
    return EMPTY;
  }

  public takeScreenshot(internalId) {
    if (this.userService.accessGranted(this.accessToken)) {
      return this.devicesDaoService.takeScreenshot(this.userStorageService.getContext(), this.userStorageService.getLang(), this.userStorageService.getUDID(), internalId).pipe(
        tap(o => !isDevMode() || console.dir(o)),
        map(o => o)
      );
    }
    return EMPTY;
  }

  public getDevicesByStatus(centerInternalIds) {
    if (this.userService.accessGranted(this.accessToken)) {
      return this.devicesDaoService.getDevicesByStatus(this.userStorageService.getContext(), this.userStorageService.getLang(), this.userStorageService.getUDID(), centerInternalIds).pipe(
        tap(o => !isDevMode() || console.dir(o)),
        map(o => o['data']['result'])
      );
    }
    return EMPTY;
  }

  public getDevicesByType(centerInternalIds) {
    if (this.userService.accessGranted(this.accessToken)) {
      return this.devicesDaoService.getDevicesByType(this.userStorageService.getContext(), this.userStorageService.getLang(), this.userStorageService.getUDID(), centerInternalIds).pipe(
        tap(o => !isDevMode() || console.dir(o)),
        map(o => o['data']['result'])
      );
    }
    return EMPTY;
  }

  public updateDeviceLicense(deviceInternalId, licenseInternalId) {
    if (this.userService.accessGranted(this.accessToken)) {
      return this.devicesDaoService.updateDeviceLicense(
        this.userStorageService.getContext(),
        this.userStorageService.getLang(),
        this.userStorageService.getUDID(),
        deviceInternalId,
        licenseInternalId
      ).pipe(
        tap(o => !isDevMode() || console.dir(o)),
        map(o => o['data']['result'])
      );
    }
    return EMPTY;
  }

  public refreshDevices() {
    this.refreshDevicesListener.next();
  }

  public refreshDevicesListen() {
    return this.refreshDevicesListener.asObservable();
  }

  public refreshDevice() {
    this.refreshDeviceListener.next();
  }

  public refreshDeviceListen() {
    return this.refreshDeviceListener.asObservable();
  }

  public filterDevices(opts) {
    this.filterDevicesListener.next(opts);
  }

  public filterDevicesListen() {
    return this.filterDevicesListener.asObservable();
  }

  public searchDevices(key) {
    this.searchDevicesListener.next(key);
  }

  public searchDevicesListen() {
    return this.searchDevicesListener.asObservable();
  }

  public exportDevices() {
    this.exportDevicesListener.next();
  }

  public exportDevicesListen() {
    return this.exportDevicesListener.asObservable();
  }

  public toggleDevicesDisposition(viewDis) {
    this.toggleDevicesDispositionListener.next(viewDis);
  }

  public toggleDevicesDispositionListen() {
    return this.toggleDevicesDispositionListener.asObservable();
  }

  private arrayToObject(array) {
    const o = new Object();
    array.forEach(i => o[i.key] = i.value);
    return o;
  }
}
